import { spawnSync, execFile } from 'node:child_process';
import { statSync } from 'node:fs';
import * as np from 'node:path';

/**
 * Host-process primitives that differ by OS — node-only (same policy as tools.shell.ts: never imported
 * from the edge-safe index). Every function keeps the macOS/Linux behaviour exactly as it was before
 * this module existed (`/bin/sh -c`, process-group signals, `ps`, `which`); the win32 branch is the
 * only new behaviour:
 *
 *   - shell: Git Bash when installed (the POSIX `sh` story a model expects — pipes, `&&`, `$VAR`,
 *     `/c/…` paths), else Windows PowerShell, else cmd. `AGENTX_SHELL` overrides (any of those, by path).
 *   - process trees: Windows has no process groups or POSIX signals, so "signal the group" is
 *     `taskkill /T` (the whole tree by parent pid), and a child is NOT `detached` (that would give it
 *     its own console window) — stdin is still ignored, so nothing can block on input.
 *   - command lookup: PATH × PATHEXT, and `.cmd`/`.bat` shims (npx, claude, codex…) are run through
 *     cmd.exe with cmd-safe quoting — node refuses to spawn them directly.
 */

export const IS_WIN = process.platform === 'win32';

/** How to run one command line in a shell. */
export interface ShellSpec {
  /** Executable to spawn. */
  bin: string;
  /** argv after `bin` for `command`. */
  args(command: string): string[];
  /** What the tool description / system prompt calls it (`/bin/sh`, `Git Bash`, …). */
  label: string;
  kind: 'sh' | 'powershell' | 'cmd';
  /** Extra spawn options this shell needs (cmd: verbatim arguments). */
  spawnOptions?: { windowsVerbatimArguments?: boolean };
}

const POSIX_SH: ShellSpec = { bin: '/bin/sh', args: (c) => ['-c', c], label: '/bin/sh', kind: 'sh' };

/** A ShellSpec for a shell binary, by its name. */
function specFor(bin: string): ShellSpec {
  const name = np.basename(bin).toLowerCase().replace(/\.exe$/, '');
  if (name === 'powershell' || name === 'pwsh') {
    // -EncodedCommand sidesteps PowerShell's own quote re-parsing of the command line entirely; the
    // preamble makes piped output UTF-8 (default: the OEM code page) and keeps progress bars off stderr.
    const pre = "$ProgressPreference='SilentlyContinue';[Console]::OutputEncoding=[Text.Encoding]::UTF8;$OutputEncoding=[Text.Encoding]::UTF8;";
    return {
      bin, kind: 'powershell', label: name === 'pwsh' ? 'PowerShell (pwsh)' : 'Windows PowerShell',
      args: (c) => ['-NoLogo', '-NoProfile', '-NonInteractive', '-ExecutionPolicy', 'Bypass', '-EncodedCommand', Buffer.from(pre + c, 'utf16le').toString('base64')],
    };
  }
  if (name === 'cmd') return { bin, kind: 'cmd', label: 'cmd.exe', args: (c) => ['/d', '/s', '/c', `"${c}"`], spawnOptions: { windowsVerbatimArguments: true } };
  return { bin, kind: 'sh', label: /bash/.test(name) && /git/i.test(bin) ? 'Git Bash' : name, args: (c) => ['-c', c] };
}

const isFile = (p: string) => { try { return statSync(p).isFile(); } catch { return false; } };

/** Git for Windows' `bin\bash.exe` (the login-env wrapper, not `usr\bin\bash.exe`), found from `git` on
 *  PATH or the standard install dirs. Never System32's bash.exe — that's the WSL launcher, a different machine. */
export function findGitBash(env: NodeJS.ProcessEnv = process.env): string | undefined {
  const roots: string[] = [];
  const git = findOnPath('git', env);
  if (git) {
    const dir = np.dirname(git);
    const up = (p: string, n: number) => { for (let i = 0; i < n; i++) p = np.dirname(p); return p; };
    // <root>\cmd\git.exe · <root>\bin\git.exe · <root>\{mingw64,ucrt64,clang64…}\bin\git.exe (Git Bash's own PATH)
    roots.push(/[\\/](mingw(32|64)|ucrt64|clang(32|64|arm64))[\\/]bin$/i.test(dir) ? up(dir, 2) : up(dir, 1));
  }
  for (const base of [env.ProgramFiles, env['ProgramFiles(x86)'], env.ProgramW6432, env.LOCALAPPDATA && np.join(env.LOCALAPPDATA, 'Programs')])
    if (base) roots.push(np.join(base, 'Git'));
  for (const r of roots) { const b = np.join(r, 'bin', 'bash.exe'); if (isFile(b)) return b; }
  return undefined;
}

let _shell: ShellSpec | undefined;
/** The shell the real Shell tool runs commands in. POSIX: `/bin/sh` (unchanged). */
export function defaultShell(): ShellSpec {
  if (!IS_WIN) return POSIX_SH;
  if (_shell) return _shell;
  const override = process.env.AGENTX_SHELL;
  if (override && (isFile(override) || findOnPath(override))) return (_shell = specFor(isFile(override) ? override : findOnPath(override)!));
  const bash = findGitBash();
  if (bash) return (_shell = specFor(bash));
  const sysRoot = process.env.SystemRoot || 'C:\\Windows';
  const ps = np.join(sysRoot, 'System32', 'WindowsPowerShell', 'v1.0', 'powershell.exe');
  if (isFile(ps)) return (_shell = specFor(ps));
  return (_shell = specFor(process.env.ComSpec || 'cmd.exe'));
}

/** The shell for an explicit `shell` option (e.g. the user's `$SHELL` for the `!` escape). POSIX: that
 *  binary with `-c`, exactly as before. Windows: honoured only when it names a real executable — Git
 *  Bash exports `SHELL=/usr/bin/bash`, an MSYS path the Windows loader can't spawn. */
export function shellFor(shell?: string): ShellSpec {
  if (!IS_WIN) return shell ? { ...POSIX_SH, bin: shell } : POSIX_SH; // label stays '/bin/sh', as the tool always said
  return shell && isFile(shell) ? specFor(shell) : defaultShell();
}

/** node's `spawn(cmd, {shell})` value for user-written command lines (hooks). POSIX: `true` (= /bin/sh,
 *  as before). Windows: the default shell — Git Bash speaks the sh these commands are written in;
 *  node passes `-c` to any non-cmd shell, which PowerShell accepts too. cmd.exe = node's own `true`. */
export function shellOption(): string | true {
  if (!IS_WIN) return true;
  const s = defaultShell();
  return s.kind === 'cmd' ? true : s.bin;
}

/** Options that detach a spawned command from the agent's terminal (see tools.shell.ts DETACHED). POSIX:
 *  its own session/process group (setsid). Windows: `detached` would open a console window, and there
 *  are no groups to lead — the child stays attached but hidden, and trees are reaped with taskkill. */
export const DETACH_OPTIONS: { detached?: boolean; windowsHide?: boolean } = IS_WIN ? { windowsHide: true } : { detached: true };

/** Signal a spawned child's whole tree. POSIX: the process group `-pid` (the child is a group leader).
 *  Windows: `taskkill /T` — by parent pid, so it must run while the root is still alive; SIGTERM and
 *  SIGKILL both force (/F): console programs have no window to receive a polite close. False when
 *  nothing was signalled (already gone). */
export function killTree(pid: number | undefined, signal: 'SIGTERM' | 'SIGKILL'): boolean {
  if (!pid) return false;
  if (!IS_WIN) {
    try { process.kill(-pid, signal); return true; } catch { return false; /* already exited / no such group */ }
  }
  try {
    const r = spawnSync('taskkill', ['/PID', String(pid), '/T', '/F'], { windowsHide: true, stdio: 'ignore', timeout: 10_000 });
    return r.status === 0;
  } catch { return false; }
}

/** Summed CPU seconds of `root` and every descendant, Windows edition of the `ps` probe (one CIM query;
 *  UserModeTime/KernelModeTime are 100ns ticks). Undefined when nothing is left or the query fails. */
export async function winTreeCpuSeconds(root: number): Promise<number | undefined> {
  const script = 'Get-CimInstance Win32_Process | ForEach-Object { "$($_.ProcessId) $($_.ParentProcessId) $([uint64]$_.UserModeTime + [uint64]$_.KernelModeTime)" }';
  const out = await new Promise<string>((res, rej) => execFile('powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', script],
    { timeout: 15_000, windowsHide: true }, (e, so) => (e ? rej(e) : res(String(so)))));
  const rows = out.split(/\r?\n/).map((l) => l.trim().split(/\s+/)).filter((r) => r.length === 3)
    .map(([pid, ppid, ticks]) => ({ pid: Number(pid), ppid: Number(ppid), sec: Number(ticks) / 1e7 }));
  const inTree = new Set(rows.filter((r) => r.pid === root).map((r) => r.pid));
  for (let grew = true; grew;) { grew = false; for (const r of rows) if (!inTree.has(r.pid) && inTree.has(r.ppid) && r.pid !== r.ppid) { inTree.add(r.pid); grew = true; } }
  let total: number | undefined;
  for (const r of rows) if (inTree.has(r.pid) && Number.isFinite(r.sec)) total = (total ?? 0) + r.sec;
  return total;
}

/** Absolute path of `cmd` on PATH (Windows: trying each PATHEXT extension), or undefined. A cmd with a
 *  directory part is checked as-is. */
export function findOnPath(cmd: string, env: NodeJS.ProcessEnv = process.env): string | undefined {
  // Windows: a bare name only matches with a PATHEXT extension — npm installs `claude` (a sh script, not
  // runnable here) right next to `claude.cmd`, and the script must not win.
  const exts = IS_WIN && !/\.[^\\/.]+$/.test(cmd) ? (env.PATHEXT || '.COM;.EXE;.BAT;.CMD').split(';').filter(Boolean) : [''];
  const cands = (base: string) => exts.map((e) => base + e);
  if (/[\\/]/.test(cmd)) return cands(cmd).find(isFile);
  const PATH = env.PATH ?? env.Path ?? '';
  for (const dir of PATH.split(np.delimiter)) {
    if (!dir) continue;
    for (const p of cands(np.join(dir, cmd))) if (isFile(p)) return p;
  }
  return undefined;
}

/** Is `cmd` runnable? POSIX: `which` (as before). Windows: PATH × PATHEXT. */
export function hasCommand(cmd: string): boolean {
  if (!IS_WIN) return spawnSync('which', [cmd]).status === 0;
  return !!findOnPath(cmd);
}

// cmd.exe metacharacters, caret-escaped (the cross-spawn rules) so an argument reaches the program verbatim.
const CMD_META = /([()\][%!^"`<>&|;, *?])/g;
function cmdArg(arg: string, shim: boolean): string {
  let a = arg.replace(/(\\*)"/g, '$1$1\\"').replace(/(\\*)$/, '$1$1');
  a = `"${a}"`.replace(CMD_META, '^$1');
  return shim ? a.replace(CMD_META, '^$1') : a; // npm's node_modules/.bin/*.cmd shims re-expand once more
}

/**
 * The argv to actually spawn for `command args…`. POSIX: unchanged. Windows: resolves the command via
 * PATH × PATHEXT; a `.cmd`/`.bat` (npx.cmd, claude.cmd, every npm/bun global shim) can't be spawned
 * directly (node: EINVAL since the 2024 batch-file CVE fix), so it runs as `cmd.exe /d /s /c "…"` with
 * every argument caret-escaped. An unresolvable command is returned as-is (spawn reports ENOENT).
 */
export function resolveSpawn(command: string, args: string[] = [], env: NodeJS.ProcessEnv = process.env): { command: string; args: string[]; windowsVerbatimArguments?: boolean } {
  if (!IS_WIN) return { command, args };
  const found = findOnPath(command, env);
  if (!found || !/\.(cmd|bat)$/i.test(found)) return { command: found ?? command, args };
  const shim = /node_modules[\\/]\.bin[\\/][^\\/]+\.cmd$/i.test(found);
  const line = [found.replace(CMD_META, '^$1'), ...args.map((a) => cmdArg(a, shim))].join(' ');
  return { command: env.ComSpec || 'cmd.exe', args: ['/d', '/s', '/c', `"${line}"`], windowsVerbatimArguments: true };
}

/**
 * A plain-object copy of `env` without the `omit` keys — for an env handed to code that reads it as an
 * object (an SDK checking `env.PATH`) rather than to spawn. POSIX: exact-key semantics, i.e. the same as
 * `({ KEY, ...rest }) => rest`. Windows: `process.env` is case-INsensitive but a copy is not — its PATH is
 * spelled `Path`, so `copy.PATH` is undefined, and an omitted key may be spelled in any case. There the
 * omission is case-insensitive and PATH is re-keyed as `PATH`.
 */
export function plainEnv(env: NodeJS.ProcessEnv, omit: string[] = []): Record<string, string> {
  const out: Record<string, string> = {};
  const drop = new Set(IS_WIN ? omit.map((k) => k.toUpperCase()) : omit);
  for (const [k, v] of Object.entries(env)) {
    if (v === undefined || drop.has(IS_WIN ? k.toUpperCase() : k)) continue;
    out[IS_WIN && k.toUpperCase() === 'PATH' ? 'PATH' : k] = v;
  }
  return out;
}

/** Last segment of a host path, for display/ids (`/a/b` → `b`; `/a/b/` → `` as before). Windows splits on `\` too. */
export const pathTail = (p: string): string | undefined => p.split(IS_WIN ? /[\\/]/ : '/').pop();

/** The editor for `$VISUAL`/`$EDITOR`-style hand-offs when neither is set. */
export const DEFAULT_EDITOR = IS_WIN ? 'notepad' : 'vi';
